import type { Condition } from '@vam/schema';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/Button.js';
import { Dialog } from '../../components/ui/Dialog.js';
import { TextArea } from '../../components/ui/Field.js';
import { parseDslJson, stringifyCondition } from '../../lib/dsl.js';

export interface DslEditorProps {
  open: boolean;
  onClose: () => void;
  value: Condition | null;
  onApply: (condition: Condition | null) => void;
  /** Validation errors returned by the API for the same document. */
  serverErrors?: { path: string; message: string }[];
}

/**
 * Advanced filter editor: raw condition DSL JSON, validated with the same
 * validator the API uses before it is applied, so the errors the console shows
 * match the ones `POST /processing-rules` would return.
 */
export function DslEditor({ open, onClose, value, onApply, serverErrors }: DslEditorProps) {
  const [text, setText] = useState(() => stringifyCondition(value ?? { all: [] }));
  const [result, setResult] = useState(() => parseDslJson(text));

  useEffect(() => {
    if (!open) return;
    const next = stringifyCondition(value ?? { all: [] });
    setText(next);
    setResult(parseDslJson(next));
  }, [open, value]);

  const errors = [
    ...(result.syntaxError ? [{ path: '', message: result.syntaxError }] : []),
    ...result.errors.map((e) => ({ path: e.path, message: e.message })),
    ...(serverErrors ?? []),
  ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title="Advanced filter"
      description="Condition DSL (docs/02 §3.5). Operators: eq, neq, in, nin, contains, starts_with, ends_with, regex, gt, lt, exists."
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onApply(null);
              onClose();
            }}
          >
            Remove filter
          </Button>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={errors.length > 0}
            onClick={() => {
              if (result.condition) onApply(result.condition);
              onClose();
            }}
          >
            Apply filter
          </Button>
        </>
      }
    >
      <TextArea
        value={text}
        rows={14}
        spellCheck={false}
        aria-label="Condition DSL JSON"
        invalid={errors.length > 0}
        className="font-mono text-[var(--text-sm)]"
        onChange={(event) => {
          setText(event.target.value);
          setResult(parseDslJson(event.target.value));
        }}
      />
      {errors.length > 0 ? (
        <ul role="alert" className="mt-2 flex flex-col gap-1">
          {errors.map((error, index) => (
            <li key={index} className="text-[var(--text-base)] text-[var(--danger)]">
              {error.path ? <code className="font-mono">{error.path}</code> : null} {error.message}
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-[var(--text-base)] text-[var(--success)]">Valid condition.</p>
      )}
    </Dialog>
  );
}
